import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useKidRoutes } from '@entities/customer'
import { useSubscriptionCancelStore } from '@entities/subscription'
import { useDisciplineStore } from '@entities/customer'
import { useOptionsDiscount } from '@entities/customer'
import { trackEvent } from '@shared/lib/amplitude'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { useModalTracking } from '../modal-tracking.hook'
import elaAvif from '../../ui/cancel/ela-cancel-upsell.avif'
import elaWebp from '../../ui/cancel/ela-cancel-upsell.webp'
import mathAvif from '../../ui/cancel/math-cancel-upsell.avif'
import mathWebp from '../../ui/cancel/math-cancel-upsell.webp'

interface ModalCrossSellDemoReturn {
  name: string
  btnText: string
  btnDeclineText: string
  title: ComputedRef<string>
  description: ComputedRef<string>
  img: ComputedRef<{ avif: string, webp: string }>
  show: ComputedRef<boolean>
  setBookDemo: () => Promise<void>
  setDecline: () => void
}

const DISCIPLINE_LABEL: Record<Discipline, string> = {
  [Discipline.ELA]: 'ELA',
  [Discipline.Math]: 'Math',
}

const DISCIPLINE_IMAGE: Record<Discipline, { avif: string, webp: string }> = {
  [Discipline.ELA]: { avif: elaAvif, webp: elaWebp },
  [Discipline.Math]: { avif: mathAvif, webp: mathWebp },
}

export const useModalCrossSellDemo = (): ModalCrossSellDemoReturn => {
  const subscriptionCancelStore = useSubscriptionCancelStore()
  const disciplineStore = useDisciplineStore()
  const discount = useOptionsDiscount()

  const { getHomeEla, getHomeMath } = useKidRoutes()

  const name = 'subscription_cancel_cross_sell_demo'
  const btnText = 'Book a free demo'
  const btnDeclineText = 'Not interested'

  const show = computed(() => {
    return subscriptionCancelStore.isModalCrossSellDemo
      && subscriptionCancelStore.isCrossSellDiscipline
  })

  const crossSellLabel = computed(() => {
    const discipline = subscriptionCancelStore.getCrossSellDiscipline
    return discipline ? DISCIPLINE_LABEL[discipline] : ''
  })

  const completedLabel = computed(() => {
    return subscriptionCancelStore.isCrossSellDisciplineEla
      ? DISCIPLINE_LABEL[Discipline.Math]
      : DISCIPLINE_LABEL[Discipline.ELA]
  })

  const img = computed(() => {
    const discipline = subscriptionCancelStore.getCrossSellDiscipline
    return discipline ? DISCIPLINE_IMAGE[discipline] : DISCIPLINE_IMAGE[Discipline.Math]
  })

  const title = computed(() => `Try ${crossSellLabel.value} lesson now & secure ${discount.value}% discount!`)

  const description = computed(() => {
    return `${subscriptionCancelStore.getKidName}’s done an amazing job in ${completedLabel.value}! `
      + `Now it’s the perfect time to discover ${crossSellLabel.value} — book a free demo & enjoy ${discount.value}% off learning.`
  })

  // The offer is about the other subject: its code replaces the tracker's, and its id is unknown (spec D8).
  const eventProps = computed(() => {
    const discipline = subscriptionCancelStore.getCrossSellDiscipline

    return {
      ...subscriptionCancelStore.getEventProps,
      feature: 'subscription',
      name,
      discipline_code: discipline ? toAnalyticsId(discipline) : null,
      discipline_id: null,
    }
  })

  const setBookDemo = async (): Promise<void> => {
    trackEvent('Form_submit', {
      ...eventProps.value,
      text: btnText,
    })

    const kidId = subscriptionCancelStore.getKidId
    const isEla = subscriptionCancelStore.isCrossSellDisciplineEla
    const url = isEla ? getHomeEla(kidId) : getHomeMath(kidId)

    if (!url) return

    subscriptionCancelStore.setClear()

    disciplineStore.setPendingLanding(isEla ? Discipline.ELA : Discipline.Math)

    await navigateTo(url)
  }

  const setDecline = (): void => {
    trackEvent('Popup_close', {
      ...eventProps.value,
      value: 'not_interested',
      text: btnDeclineText,
    })

    subscriptionCancelStore.setClear()
  }

  useModalTracking(show, () => ({ ...eventProps.value, text: title.value }))

  return {
    name,
    btnText,
    btnDeclineText,
    title,
    description,
    img,
    show,
    setBookDemo,
    setDecline,
  }
}
